export interface FaqItem {
  id: string
  question: string
  /** Plain-text answer, used for both rendering and the FAQPage structured data. */
  answer: string
  /** Optional extra link rendered after the answer. */
  link?: { to: string; label: string }
}

export interface FaqGroup {
  id: string
  eyebrow: string
  heading: string
  items: FaqItem[]
}

export const FAQ_GROUPS: FaqGroup[] = [
  {
    id: 'product',
    eyebrow: 'The product',
    heading: 'What it does, and what it refuses to do',
    items: [
      {
        id: 'forecasting-or-reporting',
        question: 'Is this a forecasting tool or a reporting tool?',
        answer:
          'Both, kept visibly apart. Recorded figures are reporting: arithmetic on documents you supplied. Projected figures are simulation: a range over the next three quarters, labelled with the method that produced it. They never merge into one number, and they never share a colour.',
      },
      {
        id: 'why-a-range',
        question: 'Why a range instead of a number?',
        answer:
          'Because a single number is read as a commitment. Three quarters out, on a handful of quarters of history, the honest output is a band with its error on the holdout printed beside it. If you want a point to plan against, take the p50 knowing what the p10 and p90 are.',
      },
      {
        id: 'empty-panels',
        question: 'Why are some panels empty?',
        answer:
          'Because the document that would support them has not arrived. Each empty panel states the reason and names what would unlock it. Filling the gap with an industry average would look identical to a real reading and there would be no way to tell them apart later.',
      },
      {
        id: 'benchmarks',
        question: 'Can you benchmark us against our industry?',
        answer:
          'Not from your ledger. A benchmark requires a comparable population measured the same way; anything else is a number with a confident label. If we ever ship benchmarks, the population and the method will be stated on the panel.',
      },
      {
        id: 'cannot-answer',
        question: 'What happens when the model cannot answer?',
        answer:
          'It abstains. Every figure in a written answer is checked against its grounding before you see it, substitution included; on failure the answer is regenerated once and then withheld with a reason.',
      },
    ],
  },
  {
    id: 'your-data',
    eyebrow: 'Your data',
    heading: 'What you send, and what happens to it',
    items: [
      {
        id: 'what-to-start',
        question: 'What do you need from us to start?',
        answer:
          'One accounting export. A trial balance makes Finance real and computes inventory cover. Beyond that: a purchase register unlocks vendor concentration, a purchase-order register with receipts unlocks lead time, a filing calendar unlocks compliance, a CRM export unlocks the pipeline panels.',
      },
      {
        id: 'clean',
        question: 'Does it need to be clean?',
        answer:
          'No — the opposite. Header on row seven, subtotals in the middle, bracketed negatives, lakh grouping and a scale note buried in a title cell are the cases the parser is built for. A cleaned-up file tells us less than a real one.',
      },
      {
        id: 'anonymised',
        question: 'Can we send it anonymised?',
        answer:
          'Yes, and for a first conversation we would prefer it. The shape is what matters, not the values. Replace names and scale the numbers; keep the layout, the notation and the calendar exactly as they are.',
      },
      {
        id: 'write-access',
        question: 'Will it write to our accounting system?',
        answer:
          'No. The connector layer verifies that a credential cannot write and refuses to save it if it can. Read-only is checked when the credential is stored, not asserted in a document.',
      },
      {
        id: 'llm-sees-ledger',
        question: 'Does a language model see our ledger?',
        answer:
          'Extraction and calculation are ordinary code, not model inference. One narrow module bridges to the forecasting engine and every import inside it is function-local, so the list of places a model can be involved is short and reviewable.',
        link: { to: '/security', label: 'Full detail on the security page.' },
      },
    ],
  },
  {
    id: 'status',
    eyebrow: 'Status and access',
    heading: 'Where this actually is',
    items: [
      {
        id: 'use-today',
        question: 'Can I use it today?',
        answer:
          'No. Phase 0 is complete: the repository boots, the vendored engine is reachable, the first migration applies and reverses, the frontend builds, and every CI gate is green and blocking. The dashboard, the setup wizard, projections, the Ask bar and connectors are not built.',
      },
      {
        id: 'design-partner',
        question: 'What is a design partner expected to do?',
        answer:
          'Send three to five real, messy exports — anonymised is fine — and answer questions about which sections you actually hold data for. In exchange the first working sections get built against your shapes rather than invented ones.',
      },
      {
        id: 'cost',
        question: 'What does it cost?',
        answer:
          'There is no price, because there is nothing to sell yet. The wider GodEngine web platform is live and currently free to all users, with billing deliberately switched off. When pricing exists it will be published rather than quoted case by case.',
      },
      {
        id: 'three-names',
        question: 'Why does the product have three different names in your own material?',
        answer:
          'It did, and that is a real defect we are fixing rather than explaining away. The name used here is Business Intelligence Simulator by GodEngine — written in full, never abbreviated.',
      },
      {
        id: 'team-size',
        question: 'How many people are behind it?',
        answer:
          'One founder, and effectively a single-author codebase. Stated plainly because it is checkable and because it is relevant to anyone deciding whether to depend on this.',
      },
    ],
  },
]

export const ALL_FAQ_ITEMS: FaqItem[] = FAQ_GROUPS.flatMap((group) => group.items)

/**
 * The five questions the home page surfaces. Home uses shorter answers than the
 * FAQ page, matching the approved copy for each surface.
 */
export const HOME_FAQ: FaqItem[] = [
  {
    id: 'home-forecasting-or-reporting',
    question: 'Is this a forecasting tool or a reporting tool?',
    answer:
      'Both, kept visibly apart. Recorded figures are reporting: arithmetic on your documents. Projected figures are simulation: a range over the next three quarters, labelled with the method that produced it. They are never blended into one number.',
  },
  {
    id: 'home-what-to-start',
    question: 'What do you need from us to start?',
    answer:
      'An accounting export is enough to make Finance real and to compute inventory cover. Each further section names the document it needs — a purchase register, a filing calendar, a CRM export — and shows nothing until it has it.',
  },
  {
    id: 'home-use-today',
    question: 'Can I use it today?',
    answer:
      'No. Phase 0 is complete and the dashboard, wizard, projections, Ask bar and connectors are not built. We are looking for design partners with messy exports, not for users.',
  },
  {
    id: 'home-write-access',
    question: 'Will it write to our accounting system?',
    answer:
      'No. The connector layer verifies that a credential cannot write and refuses to save it if it can. Read-only is enforced at the point the credential is stored, not promised in documentation.',
  },
  {
    id: 'home-cannot-answer',
    question: 'What happens when the model cannot answer?',
    answer:
      'It abstains. Every figure in a written answer is checked against its grounding before you see it; on failure the answer is regenerated once, then withheld.',
  },
]
